import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import * as React from 'react'
import { useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import * as Yup from 'yup'

import { FormikTextArea } from '@/@bothrs-ui/web/components/forms/text-area/FormikTextArea'
import { Flex, FlexColumn, Margin } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { useDoctors } from '@/modules/appointment/hooks/useDoctors'
import { PatientCommunicationStatus } from '@/modules/care-question/hooks/usePatientCommunication'
import { DocumentLayout } from '@/modules/documents/components/layout/DocumentLayout'
import { useReplyStatusPatientCommunication } from '@/modules/messages/hooks/useReplyStatusPatientCommunication'
import { useReplyToPatientCommunication } from '@/modules/messages/hooks/useReplyToPatientCommunication'
import { sortByDate } from '@/modules/shared/helpers/sort-by-date'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { VisibleScrollbarStyle } from '@/styles/scrollbar'
import { MessageDetailLayout } from '../../detail-layout/MessageDetailLayout'
import { PatientCommunicationMessage } from './PatientCommunicationMessage'

import type { FormikHelpers } from 'formik'
import type { PatientViewPatientCommunicationMessage } from '@/modules/documents/types/patient-documents.types'
import type {
  PatientCommunicationMessage as PatientCommunicationMessageType,
  PatientCommunication as PatientCommunicationType,
} from '../../../types/message.types'

type PatientCommunicationDetailProps = (
  | {
      document: PatientViewPatientCommunicationMessage
      refetch: () => void
    }
  | {
      message: PatientCommunicationType
      refetch: () => void
    }
) & {
  onAttachmentOpen?: () => void
}

type PatientCommunicationDetailInnerProps = {
  patientCommunication: PatientCommunicationType
  headerDocument: PatientViewPatientCommunicationMessage | { id: string; display: string; type: 'PatientCommunication' }
  refetch: () => void
  onAttachmentOpen?: () => void
}

const PatientCommunicationDetailInner = ({
  patientCommunication,
  headerDocument,
  refetch,
  onAttachmentOpen,
}: PatientCommunicationDetailInnerProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  const { mutate: getDoctors } = useDoctors()
  const {
    data: replyStatus,
    isLoading: isReplyStatusLoading,
    isValidating: isReplyStatusValidating,
    mutate: checkReplyStatus,
  } = useReplyStatusPatientCommunication(patientCommunication)

  const {
    replyToPatientCommunication: { isLoading: isReplying, error: replyError, execute: replyToPatientCommunication },
  } = useReplyToPatientCommunication()

  const isCheckingReplyStatus = isReplyStatusLoading || isReplyStatusValidating || replyStatus === undefined

  const sortedMessages =
    patientCommunication.messages?.sort(sortByDate<PatientCommunicationMessageType>(['createdAt'], 'ASC')) || []

  const latestMessage = sortedMessages.at(-1)
  const latestMessageFromPatient = latestMessage?.sender?.type === 'patient'
  const hasMessageFromSomeoneElse = sortedMessages.some(
    (message) => message.sender?.type && message.sender.type !== 'patient',
  )
  const hasPatientAlreadyReplied = latestMessageFromPatient && hasMessageFromSomeoneElse

  // Hooks
  // ------------------------------------------------------------------------- /
  const { showSuccessToast, showErrorToast } = useToast()

  // Methods
  // ------------------------------------------------------------------------- /
  const handleSubmit = useCallback(
    async ({ message }: { message: string }, { resetForm }: FormikHelpers<{ message: string }>) => {
      if (!message) {
        return
      }

      mixpanel.track('patient_communication_reply_sent', {
        is_mandate: isLoggedInForMandatee,
      })

      try {
        await replyToPatientCommunication(patientCommunication, message)

        showSuccessToast({
          message: t({
            id: 'patient-communication.reply-success',
            message: 'Je bericht is verstuurd',
          }),
        })

        resetForm({ values: { message: '' } })
        checkReplyStatus()
        refetch()
      } catch (error) {
        showErrorToast(error as Error)
      }
    },
    [patientCommunication, replyToPatientCommunication, showSuccessToast, showErrorToast, checkReplyStatus, refetch],
  )

  // Effects
  // ------------------------------------------------------------------------- /
  React.useEffect(() => {
    getDoctors()
    checkReplyStatus()
  }, [patientCommunication, getDoctors, checkReplyStatus])

  React.useEffect(() => {
    if (contentRef.current) {
      requestAnimationFrame(() => {
        contentRef.current?.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    }
  }, [sortedMessages.length])

  React.useEffect(() => {
    if (replyError) {
      showErrorToast(replyError)
    }
  }, [replyError, showErrorToast])

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <DocumentLayout>
      <DocumentLayout.Header document={headerDocument} />
      <DocumentLayout.Content>
        <StContent ref={contentRef}>
          <FlexColumn>
            {sortedMessages?.map((message, index) => (
              <PatientCommunicationMessage
                key={message.id ?? index}
                message={message}
                onAttachmentOpen={onAttachmentOpen}
              />
            ))}
          </FlexColumn>

          {/* Notify if no responses yet */}
          {sortedMessages.length === 1 && sortedMessages?.[0]?.sender?.type === 'patient' ? (
            <StNoResponseContainer justifyContent='center' alignItems='center' gap={theme.UI.Spacing.Space2}>
              <StLine />
              <StNoResponseText type='BodySmallRegular' color={theme.Colors.Neutrals.Dark}>
                {t({
                  id: 'patient-communication.no-response',
                  message: 'Je hebt nog geen antwoord ontvangen',
                })}
              </StNoResponseText>
              <StLine />
            </StNoResponseContainer>
          ) : null}
        </StContent>
        {isCheckingReplyStatus ? null : replyStatus === PatientCommunicationStatus.ALLOWED ? (
          <MessageDetailLayout.Footer>
            <FlexColumn alignItems='center'>
              <Formik
                initialValues={{ message: '' }}
                onSubmit={handleSubmit}
                validationSchema={Yup.object().shape({
                  message: Yup.string().nullable().max(500, `Maximum length is 500 characters.`),
                })}
                validateOnChange
                validateOnMount
              >
                {({ values }) => (
                  <StForm>
                    <StReplyTextArea
                      name='message'
                      maxLength={500}
                      required={true}
                      placeholder={t({
                        id: 'patient-communication.reply-placeholder',
                        message: 'Schrijf je volledige bericht in één keer, zoals bij een e-mail.',
                      })}
                    />
                    {/* Desktop button + compact icon button for chat-like UI */}
                    <StDesktopSendButton
                      inline
                      variant='Primary'
                      type='submit'
                      disabled={isReplying || !values.message.trim()}
                    >
                      {t({
                        id: 'patient-communication.reply-button',
                        message: 'Verstuur bericht',
                      })}
                    </StDesktopSendButton>
                    <StMobileSendButton
                      variant='Primary'
                      size='small'
                      type='submit'
                      icon={['fal', 'reply']}
                      aria-label={t({
                        id: 'patient-communication.reply-button-aria',
                        message: 'Verstuur bericht',
                      })}
                      disabled={isReplying || !values.message.trim()}
                    />
                  </StForm>
                )}
              </Formik>
            </FlexColumn>
          </MessageDetailLayout.Footer>
        ) : (
          <FlexColumn alignItems='center'>
            <StNoResponseAllowedText type='BodyMediumRegular' color={theme.Colors.Neutrals[400]}>
              {hasPatientAlreadyReplied
                ? t({
                    id: 'patient-communication.already-replied',
                    message: 'Je hebt al een bericht gestuurd. Wacht op het antwoord van de arts.',
                  })
                : t({
                    id: 'patient-communication.no-response-allowed',
                    message: `Dit bericht is informatief.
              Je kan niet antwoorden.`,
                  })}
            </StNoResponseAllowedText>
            <Margin mb={80} />
          </FlexColumn>
        )}
      </DocumentLayout.Content>
    </DocumentLayout>
  )
}

export const PatientCommunicationDetail = (props: PatientCommunicationDetailProps) => {
  const refetch = props.refetch
  const patientCommunication = 'message' in props ? props.message : props.document.patientCommunication

  // Wrapper component contains no hooks, so it can safely return early.
  if (!patientCommunication) {
    return null
  }

  const headerDocument =
    'document' in props
      ? props.document
      : {
          id: patientCommunication.id,
          display: patientCommunication.title,
          type: 'PatientCommunication' as const,
        }

  return (
    <PatientCommunicationDetailInner
      patientCommunication={patientCommunication}
      headerDocument={headerDocument}
      refetch={refetch}
      onAttachmentOpen={props.onAttachmentOpen}
    />
  )
}

const StContent = styled.div`
  ${VisibleScrollbarStyle}
  grid-area: content;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`

const StNoResponseContainer = styled(Flex)`
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StNoResponseText = styled(Span)`
  flex-shrink: 0;
`

const StNoResponseAllowedText = styled(Span)`
  text-align: center;
  white-space: break-spaces;
`

const StLine = styled.div`
  ${({ theme }) => `
    width: 100%;
    height: 1px;
    background-color: ${theme.Colors.Neutrals.Light};
  `}
`

const StForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  align-items: flex-end;
`

const StReplyTextArea = styled(FormikTextArea)`
  flex: 1;
  min-width: 0;
`

const StDesktopSendButton = styled(Button)`
  display: none;

  @media ${breakpoints.m} {
    display: flex;
  }
`

const StMobileSendButton = styled(IconButton)`
  @media ${breakpoints.m} {
    display: none;
  }
`
